import React from 'react'
import {
    StaffDashboard,
    CommitteeDashboard,
    ResearcherDashboard,
} from '../../../../../services/web/types/responseWebServiceTypes'
import { UserRole, DashboardData } from './DashBoardType'
import useTranslate from '../../../../../i18n/useTranslate'
import useWebService from '../../../../../services/web/useWebService'

const useDashboard = (role: UserRole | undefined, userGroupId: number | null | undefined, statuses?: string[]) => {
    const translate = useTranslate()
    const { getProjectDashboard } = useWebService()
    const [res, setRes] = React.useState<any>()

    React.useEffect(() => {
        let mounted = true
        ;(async () => {
            if (!userGroupId) return
            const resp = await getProjectDashboard(String(userGroupId), statuses)
            if (mounted) setRes(resp?.result)
        })()
        return () => {
            mounted = false
        }
    }, [userGroupId, statuses?.join('|')])

    const mapStaffDashboard = (r: StaffDashboard): DashboardData => {
        const reviewStepList = r.submittedStatuses.map((s) => ({
            key: s.value ? s.value.toString() : '',
            nameTh: s.nameTH,
            nameEn: s.nameEN,
            value: s.processing + s.overdue,
            overdue: +s.overdue,
        }))
        const submittedTypeList = r.submittedTypes?.map((e) => ({
            key: e.submittedTypeId.join(','),
            nameTh: e.nameTH,
            nameEn: e.nameEN,
            value: e.processing + e.overdue,
            overdue: +e.overdue,
        }))
        const projectTypeList = r.considerTypes?.map((e) => ({
            key: e.value ? e.value.toString() : '',
            nameTh: e.nameTH,
            nameEn: e.nameEN,
            value: e.processing + e.overdue,
            overdue: +e.overdue,
        }))
        const overviewList = [
            {
                key: 'year',
                name: translate.get('UM.PROJECT_DASHBOARD.PROJECTS_THIS_YEAR'),
                value: r.overview.allInCurrentYear,
                color: '#F56C94',
            },
            {
                key: 'progress',
                name: translate.get('UM.PROJECT_DASHBOARD.IN_PROGRESS'),
                value: r.overview.processing,
                color: '#FFB75E',
                filterKey: 'onlyProcessing',
            },
            // {
            //     key: 'overdue',
            //     name: translate.get('UM.PROJECT_DASHBOARD.OVERDUE'),
            //     color: '#D62E4B',
            //     star: r.overview.overdue,
            //     overdue: true,
            //     filterKey: 'onlyPendingOverdue',
            // },
        ]
        const pendingList = [
            {
                key: '1',
                name: translate.get('UM.PROJECT_DASHBOARD.PENDING'),
                value: r.pendingAndRisk.pending + r.pendingAndRisk.pendingOverDue,
                color: '#5DA8FF',
                star: r.pendingAndRisk.pendingOverDue,
                filterKey: 'onlyPendingOverdue',
            },
            {
                key: '2',
                name: translate.get('UM.PROJECT_DASHBOARD.HIGH_RISK'),
                color: '#FFD866',
                value: r.pendingAndRisk.risk,
                filterKey: 'onlyRisk',
            },
        ]
        return { reviewStepList, submittedTypeList, projectTypeList, overviewList, pendingList }
    }

    const mapCommitteeDashboard = (r: CommitteeDashboard): DashboardData => ({
        submittedTypeList: r.submittedTypes?.map((e) => ({
            key: e.submittedTypeId.join(','),
            nameTh: e.nameTH,
            nameEn: e.nameEN,
            value: e.processing + e.overdue,
            star: +e.overdue,
        })),
        projectTypeList: r.considerTypes?.map((e) => ({
            key: e.value ? e.value.toString() : '',
            nameTh: e.nameTH,
            nameEn: e.nameEN,
            value: e.processing + e.overdue,
            star: +e.overdue,
        })),
    })

    const mapResearcherDashboard = (r: ResearcherDashboard): DashboardData => ({
        overviewList: [
            {
                key: 'completed',
                name: translate.get('UM.PROJECT_DASHBOARD.COMPLETED'),
                value: r.overview.projectCompleted,
                color: '#F56C94',
            },
            {
                key: 'progress',
                name: translate.get('UM.PROJECT_DASHBOARD.IN_PROGRESS'),
                value: r.overview.projectProcessing,
                color: '#FFB75E',
            },
            {
                key: 'closed',
                name: translate.get('UM.PROJECT_DASHBOARD.CLOSED'),
                value: r.overview.projectClosed,
                color: '#3CB371',
            },
        ],
        projectStatusList: r.submittedStatuses.map((s) => ({
            key: s.nameEN,
            nameTh: s.nameTH,
            nameEn: s.nameEN,
            value: s.processing + s.overdue,
            star: s.overdue,
        })),
    })

    const dashboardData: DashboardData = React.useMemo(() => {
        if (!res || !role) return {}
        switch (role) {
            case UserRole.Staff:
            case UserRole.Board:
                return mapStaffDashboard(res as StaffDashboard)
            case UserRole.Committee:
                return mapCommitteeDashboard(res as CommitteeDashboard)
            case UserRole.Researcher:
                return mapResearcherDashboard(res as ResearcherDashboard)
            default:
                return {}
        }
    }, [role, res, translate])

    return {
        reviewStepList: dashboardData.reviewStepList ?? [],
        submittedTypeList: dashboardData.submittedTypeList ?? [],
        projectTypeList: dashboardData.projectTypeList ?? [],
        overviewList: dashboardData.overviewList ?? [],
        pendingList: dashboardData.pendingList ?? [],
        projectStatusList: dashboardData.projectStatusList ?? [],
    }
}

export default useDashboard
